import { getCLS, getFCP, getFID, getLCP, getTTFB } from "web-vitals";
import { isAndroidHybridApp } from "@catchoftheday/utilities";
import debounce from "lodash.debounce";
import {
  enableActivityTracking,
  newTracker,
  trackStructEvent,
  trackPageView,
} from "@snowplow/browser-tracker";
import { TimezonePlugin } from "@snowplow/browser-plugin-timezone";
import { GaCookiesPlugin } from "@snowplow/browser-plugin-ga-cookies";
import {
  enableFormTracking,
  FormTrackingPlugin,
} from "@snowplow/browser-plugin-form-tracking";
import {
  trackLinkClick,
  LinkClickTrackingPlugin,
} from "@snowplow/browser-plugin-link-click-tracking";
import loggy from "@catchoftheday/loggy";

import { subscribeToPathname } from "../../utilities";
import { listenToABEvents } from "../../experiments";
import { spContext } from "./context-api";
import { onDOMNodeAdded } from "./onDOMNodeAdded";

export type SnowplowConfiguration = {
  appId: string;
  collectorUrl: string;
  cookieDomain: string;
  platform: "srv" | "web" | "mob" | "pc" | "app" | "tv" | "cnsl" | "iot";
  scriptSrc: string;
  contexts: {
    webPage: boolean;
  };
};

let initialized = false;

const androidAppSnowplowConfig: Partial<SnowplowConfiguration> =
  isAndroidHybridApp()
    ? {
        platform: "srv",
      }
    : {};

// Respect legacy snowplow config whenever available
const webSnowplowConfig: Partial<SnowplowConfiguration> =
  typeof window !== "undefined" && window?.snowplowOptions
    ? window.snowplowOptions
    : {};

const TRACK_LINK_BLACKLIST = ["js-dnt", "js-product-link"];

type CleanupCallback = () => void;

export function enableLinkTracking(): CleanupCallback {
  function linkHandler(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const anchor = target?.closest("a");

    if (!anchor) {
      return;
    }

    const targetUrl = anchor.href;
    const elementId = anchor.id;
    const elementClasses = Array.from(anchor.classList);
    const elementTarget = anchor.target;
    const elementContent = anchor.innerHTML;

    if (
      elementClasses.every(
        (className) => !TRACK_LINK_BLACKLIST.includes(className)
      )
    ) {
      trackLinkClick({
        targetUrl,
        elementTarget,
        elementId,
        elementContent,
        elementClasses,
      });
    }
  }

  document.body.addEventListener("click", linkHandler);

  return () => document.body.removeEventListener("click", linkHandler);
}

export type InitializeConfig = {
  snowplowConfig: SnowplowConfiguration;
  disableAutomaticTracking: boolean;
};

export function initialize({
  disableAutomaticTracking = false,
  snowplowConfig: { collectorUrl, ...snowplowConfig },
}: InitializeConfig) {
  if (initialized) {
    return () => undefined;
  }

  if (typeof window === "undefined") {
    return;
  }

  initialized = true;
  newTracker("co", collectorUrl, {
    ...snowplowConfig,
    ...webSnowplowConfig,
    ...androidAppSnowplowConfig,
    plugins: [
      TimezonePlugin(),
      GaCookiesPlugin(),
      LinkClickTrackingPlugin(),
      FormTrackingPlugin(),
    ],
  });

  if (disableAutomaticTracking) {
    return () => undefined;
  }

  // ========================
  // Setup Automatic Trackers
  // ========================
  enableActivityTracking({ minimumVisitLength: 15, heartbeatDelay: 15 });
  const cleanupLinkTracking = enableLinkTracking();
  const cleanupFormTracking = initializeFormTracking();

  listenToABEvents();

  // Fire page view when page loads
  triggerPageView(location.pathname, location.search);
  // Fire page view whenever the pathname changes
  subscribeToPathname((pathname) => {
    triggerPageView(pathname);
  });

  reportWebVitals();

  return () => {
    cleanupLinkTracking();
    cleanupFormTracking();
  };
}

async function triggerPageView(pathname: string, queryString?: string) {
  const res = await spContext(pathname, queryString);
  if (res === null) {
    return;
  }
  const { contexts } = res;
  trackPageView({ context: contexts });
}

function reportWebVitals() {
  function reportVitals(data) {
    trackStructEvent({
      category: "Performance Metrics",
      action: data.name,
      value: data.value,
    });
  }

  getCLS(reportVitals);
  getFID(reportVitals);
  getFCP(reportVitals);
  getLCP(reportVitals);
  getTTFB(reportVitals);
}

const FORM_ELEMENTS = [
  "form",
  "input",
  "select",
  "textarea",
  "option",
  "datalist",
];

/**
 * Setups snowplow form tracking. Works by calling `enableFormTracking` from snowplow whenever a new form or field is added to the DOM.
 */
function initializeFormTracking(): CleanupCallback {
  try {
    const trackForms = debounce(() => {
      enableFormTracking({
        options: {
          forms: {
            denylist: [
              "cc-save-card",
              "account-save-card",
              "buy-form",
              "form-cart-add",
            ],
          },
        },
      });
    }, 1000);

    trackForms();

    return onDOMNodeAdded({
      elements: FORM_ELEMENTS,
      callback: trackForms,
    });
  } catch (e) {
    loggy.error(e);
    return () => undefined;
  }
}
